/**
 * Сбор ICE-кандидатов через RTCPeerConnection.
 *
 * Браузер обращается к STUN-серверам напрямую из сетевого стека, минуя
 * настройки прокси. Поэтому кандидаты — независимый источник правды о ваших
 * адресах: именно так страница узнаёт реальный IP при «включённом» VPN.
 *
 * Сбор завершается либо по окончании ICE gathering, либо по таймауту: часть
 * сетей блокирует UDP к STUN, и ждать в этом случае нечего.
 */

import type { IceCandidateInfo, WebRtcResult } from '@vpncheck/shared';

/** Сколько ждать кандидатов. Обычно хватает 1-2 секунд. */
const GATHER_TIMEOUT_MS = 5_000;

export async function collectIceCandidates(stunUrls: readonly string[]): Promise<WebRtcResult> {
  if (typeof RTCPeerConnection === 'undefined') {
    return { supported: false, candidates: [], error: 'RTCPeerConnection недоступен' };
  }
  if (stunUrls.length === 0) {
    return { supported: true, candidates: [], error: 'STUN-серверы не заданы' };
  }

  let pc: RTCPeerConnection;
  try {
    pc = new RTCPeerConnection({ iceServers: [{ urls: [...stunUrls] }] });
  } catch (err) {
    return {
      supported: false,
      candidates: [],
      error: err instanceof Error ? err.message : 'не удалось создать RTCPeerConnection',
    };
  }

  const candidates: IceCandidateInfo[] = [];
  const seen = new Set<string>();

  try {
    const gathering = new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, GATHER_TIMEOUT_MS);

      pc.onicecandidate = (event) => {
        if (!event.candidate) {
          // null-кандидат означает, что сбор закончен.
          clearTimeout(timer);
          resolve();
          return;
        }
        const info = describeCandidate(event.candidate);
        if (!info) return;
        const key = `${info.type}|${info.address}|${info.protocol}`;
        if (seen.has(key)) return;
        seen.add(key);
        candidates.push(info);
      };

      pc.onicegatheringstatechange = () => {
        if (pc.iceGatheringState === 'complete') {
          clearTimeout(timer);
          resolve();
        }
      };
    });

    // Канал данных нужен, чтобы ICE вообще начал собирать кандидатов: без
    // медиа-треков и без data channel предложение будет пустым.
    pc.createDataChannel('probe');
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await gathering;
    return { supported: true, candidates };
  } catch (err) {
    return {
      supported: true,
      candidates,
      error: err instanceof Error ? err.message : 'сбор кандидатов прерван',
    };
  } finally {
    pc.onicecandidate = null;
    pc.onicegatheringstatechange = null;
    pc.close();
  }
}

/**
 * Разбирает кандидата.
 *
 * `address` в современных браузерах заполнен, но в старых его нет — тогда адрес
 * достаётся из SDP-строки: `candidate:<foundation> <component> <proto> <pri> <address> <port> typ <type>`.
 */
function describeCandidate(candidate: RTCIceCandidate): IceCandidateInfo | null {
  const parts = candidate.candidate.split(' ');
  const address = candidate.address ?? parts[4] ?? null;
  const type = candidate.type ?? parts[7] ?? null;
  const protocol = candidate.protocol ?? parts[2] ?? 'unknown';
  if (!address || !type) return null;

  return {
    type,
    address,
    addressKind: classifyAddress(address),
    protocol: protocol.toLowerCase(),
  };
}

function classifyAddress(address: string): IceCandidateInfo['addressKind'] {
  if (address.endsWith('.local')) return 'mdns';
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(address)) return 'ipv4';
  if (address.includes(':')) return 'ipv6';
  return 'unknown';
}
